import React from "react";

import { css, styled } from "@mui/material";

import { Button } from "@/components/form/Button/Button";
import { getIsNewLeftbarDesign } from "@/lib/feature-flags";
import { returnOnCondition } from "@/utils/conditionalOutput";
import { skipForwardProp } from "@/utils/skipForwardProp";

import { useNavLinksArray } from "./useNavLinksArray";

type LinkType = ReturnType<typeof useNavLinksArray>[number];
type PropTypesOptions = {
  isMainBlockScrolledDown?: boolean;
  isActive?: boolean;
};
type PropTypes = PropTypesOptions & {
  [K in keyof LinkType]: LinkType[K];
} & {
  withHandleDrawerClose: (fn?: () => void) => () => void;
};

export const LiItem: React.FC<PropTypes> = ({
  isMainBlockScrolledDown = false,
  isActive = false,
  withHandleDrawerClose,
  ...props
}) => {
  const isNewLeftbarDesign = getIsNewLeftbarDesign();

  return (
    <li>
      {props.onClick ? (
        <RestyledButton
          variant="text"
          startIcon={props.iconComponent()}
          onClick={withHandleDrawerClose(props.onClick)}
          isMainBlockScrolledDown={isMainBlockScrolledDown}
          isActive={isActive && isNewLeftbarDesign}
        >
          {props.label}
        </RestyledButton>
      ) : props.path ? (
        <StyledLink
          href={props.path}
          isMainBlockScrolledDown={isMainBlockScrolledDown}
          isActive={isActive && isNewLeftbarDesign}
          onClick={withHandleDrawerClose()}
        >
          <div className="w-6">{props.iconComponent()}</div>
          {props.label}
        </StyledLink>
      ) : null}
    </li>
  );
};

const RestyledButton = styled(Button, {
  shouldForwardProp: skipForwardProp(["isMainBlockScrolledDown", "isActive"]),
})<PropTypesOptions>(({ theme, isMainBlockScrolledDown, isActive }) => ({
  transition: "all 0.3s ease",
  fontSize: "16px !important",
  height: "45px !important",
  ...returnOnCondition(!!isMainBlockScrolledDown, {
    fontSize: "14px !important",
    height: "40px !important",
  }),
  justifyContent: "flex-start",
  width: "100%",
  display: "flex",
  alignItems: "center",
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: "0",
    left: "-10px",
    height: "100%",
    width: "4px",
    backgroundColor: isActive ? theme.palette.green[600] : "transparent",
  },

  color: isActive ? theme.palette.green[600] : "",

  "& svg path": {
    transition: "all 0.3s ease",
    fill: isActive ? theme.palette.green[600] : theme.palette.grey1[200],
  },
  "&:hover": {
    "& svg path": {
      fill: theme.palette.green[600],
    },
    color: theme.palette.green[600],
  },
}));
// TODO: WEB-1726 replace with Link from next/link
const StyledLink = styled("a", {
  shouldForwardProp: skipForwardProp(["isMainBlockScrolledDown", "isActive"]),
})<PropTypesOptions>(({ theme, isMainBlockScrolledDown, isActive }) =>
  css({
    transition: "all 0.3s ease",
    fontSize: "16px !important",
    height: "45px",
    ...returnOnCondition(!!isMainBlockScrolledDown, {
      fontSize: "14px !important",
      height: "40px",
    }),
    display: "flex",
    alignItems: "center",
    position: "relative",

    color: isActive ? theme.palette.green[600] : "",

    "&::before": {
      content: '""',
      position: "absolute",
      top: "0",
      left: "-30px",
      height: "100%",
      width: "3px",
      backgroundColor: isActive ? theme.palette.green[600] : "transparent",
    },

    "& svg path": {
      transition: "all 0.3s ease",
      fill: isActive ? theme.palette.green[600] : theme.palette.grey1[200],
    },
    "&:hover": {
      "& svg path": {
        fill: theme.palette.green[600],
      },
      color: theme.palette.green[600],
    },
  }),
);
